import React from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {ActionButton, Button, LoadingButton, NewDatePickerField} from '@edna/components';
import {Stack} from '@edna/components/primitives';
import {formatNumber} from '@edna/utils';

import {ScrollUpButton, TBadgeColor} from 'src/components';
import {Page, useList} from 'src/components/List';
import {BigText, Error, FieldsRow, Text} from 'src/components/primitives';
import {RefreshIcon, UpDownIcon} from 'src/icons';
import {joiResolver, parseISO} from 'src/utils';

// eslint-disable-next-line no-restricted-imports
import {useCheckPayKeeperPaymentStatusMutation, useGetPayKeeperPaymentsQuery} from '../../api';
import {
  EPayKeeperPaymentStatus,
  EPaymentCorrectionType,
  TPayKeeperPayment,
  TPayKeeperPaymentPayload,
} from '../definitions';
import * as S from '../style';
import {showRefundModal} from './RefundModals';

const statusColorHash: Record<EPayKeeperPaymentStatus, TBadgeColor> = {
  [EPayKeeperPaymentStatus.NEW]: 'BLUE',
  [EPayKeeperPaymentStatus.PAYMENT_INITIATED]: 'BLUE',
  [EPayKeeperPaymentStatus.PAID]: 'GREEN',
  [EPayKeeperPaymentStatus.STUCK]: 'GREEN',
  [EPayKeeperPaymentStatus.PAYMENT_FAILED]: 'RED',
  [EPayKeeperPaymentStatus.CANCELLED]: 'RED',
  [EPayKeeperPaymentStatus.REFUND_INITIATED]: 'BLUE',
  [EPayKeeperPaymentStatus.REFUND_WITHDRAWN]: 'YELLOW',
  [EPayKeeperPaymentStatus.REFUND_WITHDRAW_FAILED]: 'RED',
  [EPayKeeperPaymentStatus.REFUND_FAILED]: 'RED',
  [EPayKeeperPaymentStatus.REFUNDING]: 'YELLOW',
  [EPayKeeperPaymentStatus.REFUNDED]: 'GREY',
  [EPayKeeperPaymentStatus.PARTIALLY_REFUNDED]: 'PURPLE',
};

const AVAILABLE_REFUND_STATUSES = [
  EPayKeeperPaymentStatus.PAID,
  EPayKeeperPaymentStatus.STUCK,
  EPayKeeperPaymentStatus.PARTIALLY_REFUNDED,
  EPayKeeperPaymentStatus.REFUND_WITHDRAW_FAILED,
  EPayKeeperPaymentStatus.REFUND_FAILED,
];

const AVAILABLE_CHECK_STATUSES = [
  EPayKeeperPaymentStatus.NEW,
  EPayKeeperPaymentStatus.PAYMENT_INITIATED,
  EPayKeeperPaymentStatus.PAID,
  EPayKeeperPaymentStatus.STUCK,
  EPayKeeperPaymentStatus.REFUND_INITIATED,
  EPayKeeperPaymentStatus.REFUND_WITHDRAWN,
  EPayKeeperPaymentStatus.REFUND_WITHDRAW_FAILED,
  EPayKeeperPaymentStatus.REFUND_FAILED,
  EPayKeeperPaymentStatus.REFUNDING,
  EPayKeeperPaymentStatus.PARTIALLY_REFUNDED,
];

const Item = React.memo<{item: TPayKeeperPayment}>(({item}) => {
  const {t} = useTranslation();

  const [checkPaymentStatus, {data: paymentStatus, isLoading}] =
    useCheckPayKeeperPaymentStatusMutation();

  const options = React.useMemo(
    () => [
      {content: t('Corrections:payments.card.refundButton'), handler: () => showRefundModal(item)},
      {
        content: t('Corrections:payments.card.partialRefundButton'),
        handler: () => showRefundModal(item, true),
      },
    ],
    [item, t],
  );

  const status = paymentStatus?.newStatus ?? item.status;

  return (
    <S.PaymentTr>
      <S.PaymentTd>{parseISO(item.createdAt, 'dd.MM.yyyy')}</S.PaymentTd>
      <S.PaymentTd>{item.tenantId}</S.PaymentTd>
      <S.PaymentTd>{item.paymentId}</S.PaymentTd>
      <S.PaymentTd>{formatNumber(item.sum)}</S.PaymentTd>
      <S.PaymentTd size="4">
        <S.PaymentBadge color={statusColorHash[status]}>{status}</S.PaymentBadge>
        {AVAILABLE_REFUND_STATUSES.includes(status) && (
          <ActionButton appearance="tertiary" square size="S" options={options}>
            <UpDownIcon />
          </ActionButton>
        )}
        {AVAILABLE_CHECK_STATUSES.includes(status) && (
          <LoadingButton
            loading={isLoading}
            appearance="primary"
            size="S"
            onClick={() => checkPaymentStatus({userId: item.userId, orderId: item.id})}
          >
            <S.MaxContentWrapper size="1" direction="ROW" align="center">
              <RefreshIcon />
              {t('Common:button.update')}
            </S.MaxContentWrapper>
          </LoadingButton>
        )}
      </S.PaymentTd>
    </S.PaymentTr>
  );
});

Item.displayName = 'CardPaymentsActionsItem';

const ErrorNode = React.memo(() => {
  const {t} = useTranslation();

  return (
    <Stack size="2">
      <BigText bottomIndent="0" secondary>
        {t('Corrections:payments.searchResults')}
      </BigText>
      <Error>
        <S.StatusIcon name="error" />
        {t(`Corrections:payments.notFound`)}
      </Error>
    </Stack>
  );
});

export const CardPaymentsActions = React.memo<{correctionType: EPaymentCorrectionType}>(() => {
  const {t} = useTranslation();

  const [canSearch, setCanSearch] = React.useState(false);

  const methods = useForm<TPayKeeperPaymentPayload>({
    mode: 'all',
    resolver: joiResolver((joi) =>
      joi.object({
        date: joi.date().required(),
        userId: joi.string().trim().required(),
      }),
    ),
  });

  const [{pages, data}, {setFilters}] = useList<TPayKeeperPayment, TPayKeeperPaymentPayload>(
    useGetPayKeeperPaymentsQuery,
    {
      filtersConfig: {validate: () => canSearch},
    },
  );

  const handleSearch = React.useCallback((values: TPayKeeperPaymentPayload) => {
    setFilters(values);
    setCanSearch(true);
  }, []);

  return (
    <FormProvider {...methods}>
      <Stack size="1" isFullWidth>
        <Text secondary size="S">
          {t('Corrections:payments.card.search')}
        </Text>
        <FieldsRow size="4" columnsWidth="auto" width="min-content">
          <S.SmallLabel htmlFor="date">{t('Corrections:payments.card.selectDate')}</S.SmallLabel>
          <NewDatePickerField name="date" />
          <S.SmallLabel htmlFor="userId">{t('Corrections:payments.card.selectUser')}</S.SmallLabel>
          <S.InputPaymentUserId noMargin name="userId" />
          <span />
          <Button appearance="primary" onClick={methods.handleSubmit(handleSearch)}>
            {t('Common:button.search')}
          </Button>
        </FieldsRow>
      </Stack>
      {canSearch && (
        <S.MaxContentWrapper isFullWidth>
          {!!data?.content?.length && (
            <>
              <ScrollUpButton />
              <S.PaymentTableHead>
                <S.PaymentTh>{t('Corrections:payments.card.table.date')}</S.PaymentTh>
                <S.PaymentTh>{t('Corrections:payments.card.table.tenant')}</S.PaymentTh>
                <S.PaymentTh>{t('Corrections:payments.card.table.paymentId')}</S.PaymentTh>
                <S.PaymentTh>{t('Corrections:payments.card.table.sum')}</S.PaymentTh>
                <S.PaymentTh>{t('Corrections:payments.card.table.status')}</S.PaymentTh>
              </S.PaymentTableHead>
            </>
          )}
          {pages.map((payload, index) => (
            <Page
              key={index}
              useQuery={useGetPayKeeperPaymentsQuery}
              payload={payload}
              renderItem={(item) => <Item item={item} />}
              noDataNode={<ErrorNode />}
              notFoundNode={<ErrorNode />}
              errorNode={<ErrorNode />}
            />
          ))}
        </S.MaxContentWrapper>
      )}
    </FormProvider>
  );
});

CardPaymentsActions.displayName = 'CardPaymentsActions';
